import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CalendarClock, CheckCircle2, ClipboardCheck, FileUp, Landmark, Play,
  RotateCcw, ShieldCheck, TestTube2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  ["dashboard", "Dashboard normativa"],
  ["fonti", "Fonti ufficiali"],
  ["versioni", "Versioni dei framework"],
  ["catalogo", "Domande e datapoint"],
  ["validazioni", "Regole di validazione"],
  ["formule", "Formule e fattori"],
  ["mappature", "Mappature tra framework"],
  ["impatti", "Analisi degli impatti"],
  ["collaudo", "Controlli e collaudo"],
  ["programmazioni", "Aggiornamenti programmati"],
  ["storico", "Storico pubblicazioni"],
  ["rollback", "Rollback"],
];

const STATE_STYLE = {
  DRAFT: "border-amber-300 bg-amber-50 text-amber-800",
  CONFIGURED: "border-sky-300 bg-sky-50 text-sky-800",
  IMPACT_ANALYZED: "border-indigo-300 bg-indigo-50 text-indigo-800",
  TESTED: "border-teal-300 bg-teal-50 text-teal-800",
  APPROVED: "border-violet-300 bg-violet-50 text-violet-800",
  SCHEDULED: "border-blue-300 bg-blue-50 text-blue-800",
  ACTIVATING: "border-orange-300 bg-orange-50 text-orange-800",
  ACTIVE: "border-emerald-300 bg-emerald-50 text-emerald-800",
  TEST_FAILED: "border-red-300 bg-red-50 text-red-800",
  ACTIVATION_FAILED: "border-red-300 bg-red-50 text-red-800",
  ROLLED_BACK: "border-slate-300 bg-slate-100 text-slate-700",
};

const EMPTY_SOURCE = {
  authority: "",
  source_type: "REGOLAMENTO",
  official_url: "",
  jurisdiction: "UE",
};

const EMPTY_UPDATE = {
  source_id: "",
  title: "",
  authority: "",
  source_type: "REGOLAMENTO",
  source_number: "",
  framework: "VSME",
  version: "",
  official_url: "",
  publication_date: "",
  effective_from: "",
  description: "",
  territorial_scope: "UE",
  is_test: true,
};

function StateBadge({ state }) {
  return (
    <Badge
      variant="outline"
      className={STATE_STYLE[state] || "border-slate-300 bg-slate-50 text-slate-700"}
      data-testid={`normativa-state-${state}`}
    >
      {state}
    </Badge>
  );
}

export default function AdminNormativa() {
  const location = useLocation();
  const navigate = useNavigate();
  const currentTab = useMemo(() => {
    const slug = location.pathname.split("/").filter(Boolean).at(-1);
    return TABS.some(([id]) => id === slug) ? slug : "dashboard";
  }, [location.pathname]);
  const [overview, setOverview] = useState(null);
  const [sources, setSources] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [sourceForm, setSourceForm] = useState(EMPTY_SOURCE);
  const [updateForm, setUpdateForm] = useState(EMPTY_UPDATE);
  const [selected, setSelected] = useState(null);
  const [configuration, setConfiguration] = useState("[]");
  const [documentFile, setDocumentFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [overviewResponse, sourceResponse, updateResponse] = await Promise.all([
        api.get("/admin/normativa/overview"),
        api.get("/admin/normativa/sources"),
        api.get("/admin/normativa/updates"),
      ]);
      setOverview(overviewResponse.data);
      setSources(sourceResponse.data.sources || []);
      setUpdates(updateResponse.data.updates || []);
    } catch (error) {
      toast.error("Centro normativo non disponibile", { description: formatApiError(error) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openUpdate = async (updateId) => {
    try {
      const { data } = await api.get(`/admin/normativa/updates/${updateId}`);
      setSelected(data);
      setConfiguration(JSON.stringify(data.update.configuration?.changes || [], null, 2));
    } catch (error) {
      toast.error("Dettaglio non disponibile", { description: formatApiError(error) });
    }
  };

  const saveSource = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/normativa/sources", sourceForm);
      setSourceForm(EMPTY_SOURCE);
      await load();
      toast.success("Fonte ufficiale registrata");
    } catch (error) {
      toast.error("Registrazione fonte fallita", { description: formatApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  const saveUpdate = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/normativa/updates", updateForm);
      setUpdateForm(EMPTY_UPDATE);
      await load();
      toast.success("Versione normativa creata in bozza");
    } catch (error) {
      toast.error("Creazione versione fallita", { description: formatApiError(error) });
    } finally {
      setBusy(false);
    }
  };

  const configure = async () => {
    if (!selected) return;
    try {
      const changes = JSON.parse(configuration);
      await api.put(`/admin/normativa/updates/${selected.update.id}/configuration`, {
        changes,
        transition_rule: "FUTURE_PERIODS_ONLY",
        activation_timezone: "Europe/Rome",
        activation_time: "00:00",
      });
      await openUpdate(selected.update.id);
      await load();
      toast.success("Configurazione versionata");
    } catch (error) {
      toast.error("Configurazione non valida", { description: formatApiError(error) });
    }
  };

  const action = async (path, payload = null) => {
    if (!selected) return;
    try {
      await api.post(`/admin/normativa/updates/${selected.update.id}/${path}`, payload);
      await openUpdate(selected.update.id);
      await load();
      toast.success("Operazione registrata");
    } catch (error) {
      toast.error("Operazione bloccata", { description: formatApiError(error) });
    }
  };

  const startUpdate = async () => {
    if (!selected?.gate.passed || selected.update.state !== "APPROVED") return;
    const confirmed = window.confirm(
      "Confermi la programmazione dell'aggiornamento normativo? "
      + "L'attivazione avverrà esclusivamente alla data di entrata in vigore.",
    );
    if (confirmed) await action("start", { confirm: true });
  };

  const uploadDocument = async () => {
    if (!selected || !documentFile) return;
    try {
      const body = new FormData();
      body.append("file", documentFile);
      await api.post(`/admin/normativa/updates/${selected.update.id}/documents`, body);
      setDocumentFile(null);
      await openUpdate(selected.update.id);
      toast.success("Documento ufficiale archiviato in privato");
    } catch (error) {
      toast.error("Upload documento fallito", { description: formatApiError(error) });
    }
  };

  const updateCheck = async (code) => {
    if (!selected) return;
    try {
      await api.put(`/admin/normativa/updates/${selected.update.id}/checklist/${code}`, {
        status: "PASSED",
        note: "Controllo effettuato dal Superadmin",
        evidence: "Verifica registrata nella console normativa",
      });
      await openUpdate(selected.update.id);
    } catch (error) {
      toast.error("Checklist non aggiornata", { description: formatApiError(error) });
    }
  };

  if (!overview) {
    return <p className="text-sm text-slate-600" data-testid="normativa-loading">Caricamento normativa…</p>;
  }

  return (
    <div className="space-y-8" data-testid="normativa-command-center">
      <header className="border-b border-slate-200 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-xs font-semibold uppercase text-emerald-700">Superadmin · Catalogo centrale</p>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">Normativa e standard</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Versioni, dipendenze e pubblicazioni controllate. Nessuna modifica entra in vigore
              senza impatto, collaudo, checklist, approvazione e un checkpoint ripristinabile.
            </p>
          </div>
          <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-800">
            Archivio privato · KMS provider
          </Badge>
        </div>
        <nav className="mt-6 flex flex-wrap gap-2" aria-label="Sezioni normativa">
          {TABS.map(([id, label]) => (
            <Link
              key={id}
              to={id === "dashboard" ? "/admin/normativa" : `/admin/normativa/${id}`}
              data-testid={`normativa-tab-${id}`}
              className={`shrink-0 border-b-2 px-2 py-2 text-xs font-semibold transition-colors ${
                currentTab === id
                  ? "border-emerald-700 text-emerald-800"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>
      </header>

      {currentTab === "dashboard" && (
        <section className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {Object.entries(overview.updates_by_state).map(([state, count]) => (
              <Card key={state} className="border-slate-200" data-testid={`normativa-counter-${state}`}>
                <CardContent className="pt-4"><StateBadge state={state} /><p className="mt-3 text-3xl font-bold">{count}</p></CardContent>
              </Card>
            ))}
            {Object.keys(overview.updates_by_state).length === 0 && (
              <p className="col-span-full text-sm text-slate-500" data-testid="normativa-empty-state">Nessun aggiornamento aperto.</p>
            )}
          </div>
          <Card className="border-slate-200" data-testid="normativa-create-update-card">
            <CardHeader><CardTitle className="text-base">Registra versione normativa di prova</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={saveUpdate} className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <select
                  required
                  value={updateForm.source_id}
                  onChange={(event) => setUpdateForm({ ...updateForm, source_id: event.target.value })}
                  data-testid="normativa-update-source"
                  className="h-10 border border-slate-300 bg-white px-3 text-sm"
                >
                  <option value="">Fonte ufficiale</option>
                  {sources.map((source) => <option key={source.id} value={source.id}>{source.authority}</option>)}
                </select>
                {[["title", "Titolo ufficiale"], ["authority", "Autorità"], ["source_number", "Numero o codice"], ["framework", "Framework"], ["version", "Versione"], ["official_url", "URL ufficiale"], ["publication_date", "Pubblicazione", "date"], ["effective_from", "Entrata in vigore", "date"]].map(([key, label, type]) => (
                  <Input key={key} required={key !== "publication_date"} type={type || "text"} placeholder={label} value={updateForm[key] || ""}
                    onChange={(event) => setUpdateForm({ ...updateForm, [key]: event.target.value })}
                    data-testid={`normativa-update-${key}`} />
                ))}
                <Textarea className="md:col-span-3" placeholder="Descrizione normativa" value={updateForm.description}
                  onChange={(event) => setUpdateForm({ ...updateForm, description: event.target.value })}
                  data-testid="normativa-update-description" />
                <Button type="submit" disabled={busy} className="w-fit bg-emerald-700 hover:bg-emerald-800" data-testid="normativa-update-create">
                  Crea bozza
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      )}

      {currentTab === "fonti" && (
        <Card className="border-slate-200" data-testid="normativa-sources-panel">
          <CardHeader><CardTitle className="text-base">Fonti ufficiali</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <form onSubmit={saveSource} className="grid gap-3 md:grid-cols-4">
              {[["authority", "Autorità"], ["source_type", "Tipologia"], ["official_url", "URL ufficiale"], ["jurisdiction", "Territorio"]].map(([key, label]) => (
                <Input key={key} required placeholder={label} value={sourceForm[key]}
                  onChange={(event) => setSourceForm({ ...sourceForm, [key]: event.target.value })}
                  data-testid={`normativa-source-${key}`} />
              ))}
              <Button type="submit" disabled={busy} className="w-fit bg-emerald-700" data-testid="normativa-source-create">Aggiungi fonte</Button>
            </form>
            {sources.map((source) => <div key={source.id} className="border-t border-slate-100 pt-3 text-sm" data-testid={`normativa-source-row-${source.id}`}><strong>{source.authority}</strong> · {source.source_type} · {source.jurisdiction}</div>)}
          </CardContent>
        </Card>
      )}

      {currentTab !== "dashboard" && currentTab !== "fonti" && (
        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
          <Card className="border-slate-200" data-testid="normativa-updates-panel">
            <CardHeader><CardTitle className="text-base">Versioni e pubblicazioni</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {updates.map((update) => (
                <button key={update.id} type="button" onClick={() => openUpdate(update.id)} data-testid={`normativa-update-open-${update.id}`}
                  className="w-full border border-slate-200 p-4 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/30">
                  <div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-slate-900">{update.title}</p><p className="mt-1 text-xs text-slate-500">{update.framework} · {update.version} · entrata: {update.effective_from || "da definire"}</p></div><StateBadge state={update.state} /></div>
                </button>
              ))}
              {updates.length === 0 && <p className="text-sm text-slate-500">Nessuna versione disponibile.</p>}
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="normativa-detail-panel">
            <CardHeader><CardTitle className="text-base">{selected ? selected.update.title : "Seleziona una versione"}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {!selected && <p className="text-sm text-slate-500">Apri una versione per vedere impatto, checklist, job e rollback.</p>}
              {selected && <>
                <div className="flex flex-wrap gap-2"><StateBadge state={selected.update.state} /><Badge variant="outline">Revisione {selected.update.revision}</Badge></div>
                <Textarea value={configuration} onChange={(event) => setConfiguration(event.target.value)} rows={8} data-testid="normativa-changes-json" aria-label="Modifiche strutturate JSON" />
                <Button variant="outline" onClick={configure} data-testid="normativa-configure"><ClipboardCheck className="mr-2 h-4 w-4" />Configura</Button>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => action("impact-analysis")} data-testid="normativa-impact"><Landmark className="mr-2 h-4 w-4" />Analizza impatto</Button>
                  <Button variant="outline" onClick={() => action("test")} data-testid="normativa-test"><TestTube2 className="mr-2 h-4 w-4" />Esegui test</Button>
                  <Button variant="outline" onClick={() => action("approve")} data-testid="normativa-approve"><CheckCircle2 className="mr-2 h-4 w-4" />Approva</Button>
                  <Button
                    className="bg-emerald-700 hover:bg-emerald-800"
                    disabled={!selected.gate.passed || selected.update.state !== "APPROVED"}
                    onClick={startUpdate}
                    data-testid="normativa-start"
                  >
                    <Play className="mr-2 h-4 w-4" />Avvia aggiornamento
                  </Button>
                  <Button variant="outline" onClick={() => action("rollback", { reason: "Rollback amministrativo richiesto dopo controllo" })} data-testid="normativa-rollback"><RotateCcw className="mr-2 h-4 w-4" />Rollback</Button>
                </div>
                <div className="border-t border-slate-100 pt-4"><p className="text-xs font-semibold uppercase text-slate-500">Checklist obbligatoria</p><div className="mt-2 grid gap-2">{overview.checklist_codes.map((code) => { const row = selected.checklist.find((item) => item.code === code); return <button key={code} type="button" onClick={() => updateCheck(code)} data-testid={`normativa-check-${code}`} className="flex items-center justify-between border border-slate-200 px-3 py-2 text-left text-xs"><span>{code}</span><Badge variant="outline">{row?.status || "PENDING"}</Badge></button>; })}</div></div>
                {selected.update.state === "DRAFT" && (
                  <div className="border-t border-slate-100 pt-4">
                    <Label htmlFor="normativa-document-file">Documento ufficiale privato</Label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Input
                        id="normativa-document-file"
                        type="file"
                        accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={(event) => setDocumentFile(event.target.files?.[0] || null)}
                        data-testid="normativa-document-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!documentFile}
                        onClick={uploadDocument}
                        data-testid="normativa-document-upload"
                      >
                        <FileUp className="mr-2 h-4 w-4" />Allega documento
                      </Button>
                    </div>
                  </div>
                )}
                <div className="border-t border-slate-100 pt-4 text-xs text-slate-600"><p><strong>Impatto:</strong> {selected.impact?.affected_sections?.length || 0} sezioni · {selected.impact?.affected_companies || 0} aziende · {selected.impact?.score_runs_to_invalidate || 0} ScoreRun</p><p className="mt-1"><strong>Gate:</strong> {selected.gate.passed ? "completo" : `${selected.gate.missing.length} controlli mancanti`}</p></div>
              </>}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}